/**
 * @author AgentStore
 * @description 全局心跳检测工具类 - 用于监测用户登录状态和身份变更
 */
import { verifyUserHeartbeat } from '@/api/user'
import {getAccessToken, removeAccessToken} from '@/utils/accessToken'
import { responsiveConfirm } from '@/utils/responsiveSystemDialog'
import router from '@/router'
import store from '@/store'
import i18n from '@/locales/i18n'  // 导入 i18n 实例

// 心跳检测配置
const HEARTBEAT_INTERVAL = 60 * 1000 // 60秒检测一次
let heartbeatTimer = null
let isChecking = false
let lastKnownToken = null // 记录上次检查的token
let isShowingDialog = false // 防止重复弹窗的标志位

// 定义需要排除心跳检测和弹窗的路径前缀
const EXCLUDE_PATH_PREFIXES = ['/login', '/register', '/chat-share', '/web_preview']

/**
 * @author AgentStore
 * @description 检查token是否发生变化（多窗口同步检查）
 * @returns {boolean} 如果token发生变化返回true
 */
function checkTokenChange() {
  const currentToken = getAccessToken()
  const storeToken = store.getters['user/accessToken']

  if (currentToken !== storeToken) {
    if (currentToken) {
      store.commit('user/setAccessToken', currentToken)
      lastKnownToken = currentToken
      return true
    } else {
      removeAccessToken()
      return true
    }
  }

  // 记录当前token
  if (currentToken !== lastKnownToken) {
    lastKnownToken = currentToken
    return true
  }

  return false
}

/**
 * @author AgentStore
 * @description 检查token有效性和用户身份
 * @returns {Promise<boolean>}
 */
async function checkUserTokenAndIdentity() {
  try {
    if (isChecking || isShowingDialog) return true

    isChecking = true

    checkTokenChange()

    // 调用心跳检测接口
    const response = await verifyUserHeartbeat()

    if (response && response.data) {
      const { auth_result, user_id } = response.data

      // 检查认证结果
      if (auth_result === 1 && user_id) {
        // 获取当前存储的用户ID
        const currentUserId = store.getters['user/user_id']

        // 比较用户ID是否一致
        if (currentUserId && user_id !== currentUserId) {
          await showUserIdentityChangedDialog()
          return false
        }

        return true
      } else {
        return false
      }
    } else {
      return false
    }

  } catch (error) {
    return false
  } finally {
    isChecking = false
  }
}



/**
 * 检查当前路径是否需要排除心跳检测和弹窗
 * @returns {boolean} 是否需要排除
 */
function shouldExcludeCurrentPath() {
  const currentPath = router.currentRoute.value.path
  return EXCLUDE_PATH_PREFIXES.some(prefix => currentPath.startsWith(prefix))
}



/**
 * @author AgentStore
 * @description 显示用户身份变更弹窗
 * @returns {Promise<void>}
 */
export async function showUserIdentityChangedDialog() {
  // 检查当前路径是否需要排除弹窗
  if (shouldExcludeCurrentPath()) {
    return
  }

  // 防止重复弹窗
  if (isShowingDialog) {
    return
  }

  try {
    isShowingDialog = true

    // 暂停心跳检测，防止在弹窗期间继续触发
    stopHeartbeat()

    await responsiveConfirm(
      i18n.global.t('common.account_identity_changed_refresh_required'),
      i18n.global.t('common.tips'),
      {
        confirmButtonText: i18n.global.t('common.refresh'),
        type: 'warning',
        showCancelButton: false,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        center: false
      }
    ).then(() => {
      window.location.reload()
    }).catch(() => {
      // 用户取消或其他情况，重置状态
      isShowingDialog = false
      // 可以选择是否重新启动心跳检测
      // startHeartbeat()
    })

  } catch (error) {
    console.error('显示用户身份变更弹窗失败:', error)
    isShowingDialog = false
    // 发生错误时也重置状态
    // 可以选择是否重新启动心跳检测
    // startHeartbeat()
  }
}

/**
 * @author AgentStore
 * @description 执行心跳检测
 * @returns {Promise<void>}
 */
async function executeHeartbeat() {
  if (shouldExcludeCurrentPath()) {
    stopHeartbeat()
    return
  }

  // 如果正在显示弹窗，跳过本次检测
  if (isShowingDialog) {
    return
  }

  // 执行检测
  const isValid = await checkUserTokenAndIdentity()

  if (!isValid) {
    // await handleForceLogout()
  }
}

/**
 * @author AgentStore
 * @description 开始心跳检测
 * @returns {void}
 */
export function startHeartbeat() {
  // 避免重复启动
  if (heartbeatTimer) {
    return
  }

  // 重置弹窗状态
  isShowingDialog = false

  // 立即执行一次检测
  executeHeartbeat()

  // 设置定时器
  heartbeatTimer = setInterval(executeHeartbeat, HEARTBEAT_INTERVAL)
}

/**
 * @author AgentStore
 * @description 停止心跳检测
 * @returns {void}
 */
export function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

/**
 * @author AgentStore
 * @description 获取心跳检测状态
 * @returns {boolean}
 */
export function getHeartbeatStatus() {
  return !!heartbeatTimer
}

/**
 * @author AgentStore
 * @description 重启心跳检测
 * @returns {void}
 */
export function restartHeartbeat() {
  stopHeartbeat()
  // 重置弹窗状态
  isShowingDialog = false
  startHeartbeat()
}

/**
 * @author AgentStore
 * @description 重置弹窗状态（供外部调用）
 * @returns {void}
 */
export function resetDialogState() {
  isShowingDialog = false
}
